// packages/engine/src/lifecycle-diagnostics.ts
//
// Scrub-relative reference diagnostics.
//
// The severity of "X calls Y" depends on WHERE you stand on the timeline, which
// is what makes `archlang check --at 2026-Q3` — "will this model be valid in
// Q3?" — a question worth asking. It also replaces the two-keyword deprecation
// design: the level falls out of the scrub position instead of needing a second
// word.
//
// §10.1 classes: `error` fails the gate, `warning` reports without failing,
// advisory is a report artifact (spelled `info` in the severity enum).

import { makeDiag } from "./diagnostics/catalog.js";
import type { Diagnostic } from "./types.js";
import type { DependencyGraph } from "./graph.js";
import { isCallEdge } from "./graph.js";
import type { NodeUniverse } from "./selector-eval.js";
import {
  argText,
  argValue,
  lifecycleStateAt,
  type DecorationRegistry,
  type DecorationInstance,
  type LifecycleState,
} from "./decorations.js";
import { EMPTY_TIMELINE, labelAt, type Timeline } from "./timeline.js";

export interface LifecycleRefOptions {
  /** The scrub point (a timeline ordinal) to judge references at. Defaults to
   *  the timeline's `now`. */
  readonly at?: number;
  /** The model's derived timeline — supplies the default scrub point and lets a
   *  report echo the author's own marker instead of a number. */
  readonly timeline?: Timeline;
  /** Needed to read `@deprecated(use: …)` off the callee, so the warning can
   *  name the replacement instead of just the problem. */
  readonly registry?: DecorationRegistry;
}

/**
 * Walk every call edge and report references that do not hold at `at`.
 *
 * Only references FROM a live element are reported: two planned things
 * depending on each other is a normal roadmap, not a finding, and a deprecated
 * element still calling another deprecated element is already covered by its
 * own deprecation.
 */
export function lifecycleReferenceDiagnostics(
  graph: DependencyGraph,
  universe: NodeUniverse,
  opts: LifecycleRefOptions = {},
): Diagnostic[] {
  const timeline = opts.timeline ?? EMPTY_TIMELINE;
  const t = opts.at ?? timeline.now;
  const out: Diagnostic[] = [];

  const stateOf = (key: string): { state: LifecycleState; deadTo?: number } | undefined => {
    const w = universe.byKey(key)?.lifecycle;
    if (!w) return undefined;
    return { state: lifecycleStateAt(w, t), deadTo: w.deadTo };
  };

  for (const edge of graph.edges) {
    if (!isCallEdge(edge)) continue;
    const fromKey = edge.fromModulePath.join(".");
    const toKey = edge.toModulePath.join(".");
    // An undecorated caller is live at every point — that IS the common case,
    // and exactly the one worth reporting against.
    const from = stateOf(fromKey)?.state ?? "live";
    if (from !== "live") continue;
    const to = stateOf(toKey);
    if (!to) continue;

    const source = universe.byKey(fromKey)?.source;
    if (to.state === "retired") {
      out.push(
        makeDiag("error", source, "LIFECYCLE_REF_RETIRED", "validation.lifecycleRefRetired", {
          target: toKey,
          at: markerOf(timeline, to.deadTo) ?? "its retirement",
        }),
      );
    } else if (to.state === "deprecated") {
      // Naming the replacement is the whole reason `@deprecated` carries `use`
      // — a warning that only says "this is deprecated" makes the reader go
      // find the successor by hand.
      const use = useHintFor(universe.byKey(toKey)?.decorations, opts);
      out.push(
        makeDiag("warning", source, "LIFECYCLE_REF_DEPRECATED", "validation.lifecycleRefDeprecated", {
          target: toKey,
          until: markerOf(timeline, to.deadTo) ? ` until ${markerOf(timeline, to.deadTo)}` : "",
          useHint: use ? ` — use ${use} instead` : "",
        }),
      );
    } else if (to.state === "planned") {
      // The killer check: to-be leaking into as-is. A live element cannot
      // depend on something that is not built yet.
      out.push(
        makeDiag("warning", source, "LIFECYCLE_REF_PLANNED", "validation.lifecycleRefPlanned", {
          target: toKey,
        }),
      );
    }
  }
  return out;
}

/** The author's own marker for an ordinal — never a date we invented for it. */
function markerOf(timeline: Timeline, ordinal: number | undefined): string | undefined {
  return ordinal === undefined ? undefined : labelAt(timeline, ordinal);
}

/** The `use:` argument of a `@deprecated`, when one was written and the caller
 *  supplied a registry to read the schema through. */
function useHintFor(
  decorations: ReadonlyMap<string, DecorationInstance> | undefined,
  opts: LifecycleRefOptions,
): string | undefined {
  const inst = decorations?.get("deprecated");
  const decl = inst && opts.registry?.kinds.get(inst.name);
  return decl ? argText(argValue(inst, decl, "use")) : undefined;
}
